import { MutableRefObject, useEffect, useRef } from "react";
import { useWindowPostMessage } from "./UseWindowPostMessage";
import { EventKey } from "@emed-labs/virtual-lab-models";

export interface UseEventListenerProps {
	[key: string]: EventKey<any>;
}

export interface UseEventAddRemoveListenerMethods {
	addListener: <S>(key: EventKey<S>, callback: (data: S) => void) => () => void;
	removeListener: <S>(key: EventKey<S>, callback: (data: S) => void) => void;
}

export interface UseEventListenerMethods extends UseEventAddRemoveListenerMethods {
	dispatchEvent: <S>(key: EventKey<S>, data: S, postMessageTargetWindow?: Window) => void;
}

/**
 * An event listener hook that can be used to add/remove listeners and trigger events
 *
@example
interface AUDIO_VIDEO_ERROR_EVENT{
    error: {
		message: string;
		code?: string;
	};
	type?: "microphone" | "speaker" | "camera";
	direction?: "send" | "receive";
}

const events = {
	AUDIO_VIDEO_ERROR: new EventKey<AUDIO_VIDEO_ERROR_EVENT>("audioVideoError")
}

const { addListener, removeListener, dispatchEvent } = useEventListener(events);

//....somewhere else in the code
dispatchEvent(events.AUDIO_VIDEO_ERROR, {

})
 * @param props UseEventListenerProps
 * @returns
 */

export const useEventListener = (
	props: UseEventListenerProps,
	allowedMessageOrigins = [window.location.origin],
	defaultPostMessageIframe?: MutableRefObject<HTMLIFrameElement>
): UseEventListenerMethods => {
	const eventListeners = useRef<Record<string, any>>({} as any);
	const { sendMessage: sendWindowPostMessage } = useWindowPostMessage({
		allowedMessageOrigins,
		onMessage: (message) => {
			if (message?.payload?.key) {
				dispatch(message.payload.key, message.payload.data);
			}
		}
	});
	function addListener<S>(key: EventKey<S>, callback: (data: S) => void) {
		if (!eventListeners.current[key.id]) {
			eventListeners.current[key.id] = [];
		}
		eventListeners.current[key.id].push(callback);
		return () => {
			removeListener<S>(key, callback);
		};
	}

	function removeListener<S>(key: EventKey<S>, callback: (data: S) => void) {
		if (eventListeners.current[key.id]) {
			eventListeners.current[key.id] = eventListeners.current[key.id].filter((cb: any) => cb !== callback);
		}
	}

	function dispatch<S>(key: EventKey<S>, data: S, postMessageTargetWindow?: Window) {
		if (!key.id) {
			throw new Error("key must have an id");
		}
		postMessageTargetWindow = postMessageTargetWindow || defaultPostMessageIframe?.current?.contentWindow || undefined;
		if (eventListeners.current[key.id as string]) {
			eventListeners.current[key.id as string].forEach((callback: any) => callback(data));
		}
		if (postMessageTargetWindow) {
			sendWindowPostMessage({ message: { key, data }, targetWindow: postMessageTargetWindow });
		}
	}

	function dispatchEvent<S>(key: EventKey<S>, data: S, postMessageTargetWindow?: Window) {
		return dispatch(key, data, postMessageTargetWindow);
	}

	useEffect(() => {
		return () => {
			// Clean up the event listener (unsubscribe, close WebSocket, etc.)
			// Here we'll just clear the eventListeners.current object
			Object.keys(eventListeners.current).forEach((key) => {
				delete eventListeners.current[key];
			});
		};
	}, []);

	return { addListener, removeListener, dispatchEvent };
};
